
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import DepartmentRepositoryInterface from '@/services/Departments/Interfaces';
import { FilterRequestData } from '@/services/QC-V2/chat/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';

interface FilterItemInterface {
  text: string,
  value: string | number | boolean | undefined,
}

enum FilterType {
  MENU,
  AUTOCOMPLETE,
  CHECKBOX,
  INPUT,
  COMBOBOX,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  type: FilterType,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}


@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'userRepository',
    'departmentRepository',
    'tagRepositoryV2',
  ],
})
export default class ChatsFilters extends Vue {
  public static CHAT_QUEUE_ROUTE = 'chat_queue';
  public static CHAT_ARCHIVE_ROUTE = 'chat_archive';

  private userRepository!: UserRepoInterface;
  private departmentRepository!: DepartmentRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private selectedFilters: FilterInterface[] = [];

  private filters: FilterInterface[] = [];

  private customDates: string[] = [];

  private tags: TagInterface[] = [];

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      await this.getSupportAgents();
      this.addIdFilter();
      this.addDatesFilter();
      this.addRatesFilter();
      this.addDraftsFilter();
      await this.addTagsFilter();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private keepCustomPeriodActive(filter: FilterInterface, index: number): void {
    setTimeout(() => {
      if (filter.value === 'chat.endedTimestamp' && filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
        this.selectedFilters[index] = filter;
      }
    }, 10);
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  private async getSupportAgents(): Promise<void> {
    const departments = await this.departmentRepository.getAllDepartments();
    const supportDepartment = departments.find((department) => department.name.toLowerCase() === 'support');
    if (!supportDepartment) {
      this.$errorDialog.show('\'Support\' Department not found...');
      return;
    }

    const supportAgents = await this.userRepository.getUsersByDepartment(supportDepartment._id);
    this.filters.push(
      {
        text: 'Agent',
        value: 'agent.email',
        icon: 'mdi-account',
        type: FilterType.AUTOCOMPLETE,
        items: supportAgents.map((agent) => ({ text: agent.email, value: agent.email })),
        selected: { text: '', value: '' },
      },
    );
  }

  private addIdFilter(): void {
    const filter: FilterInterface = {
      text: 'Chat ID',
      value: 'chat.id',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'chat.id', value: '' },
    };

    this.filters.push(filter);
  }

  private addDatesFilter(): void {
    const dateFilter: FilterInterface = {
      text: 'Date',
      value: 'chat.endedTimestamp',
      icon: 'mdi-calendar',
      type: FilterType.MENU,
      items: [
        { text: 'TODAY', value: 'today' },
        { text: 'YESTERDAY', value: 'yesterday' },
        { text: 'LAST 7 DAYS', value: '7 days' },
        { text: 'LAST 30 DAYS', value: '30 days' },
        { text: 'LAST MONTH', value: 'last month' },
        { text: 'CURRENT MONTH', value: 'current month' },
        { text: 'CUSTOM PERIOD', value: 'custom period' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(dateFilter);
  }

  private addRatesFilter(): void {
    const rateFilter: FilterInterface = {
      text: 'Rate',
      value: 'chat.rate',
      icon: 'mdi-thumbs-up-down-outline',
      type: FilterType.MENU,
      items: [
        { text: 'NOT RATED', value: '0' },
        { text: 'RATED BAD', value: '1' },
        { text: 'RATED GOOD', value: '2' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(rateFilter);
  }

  private addDraftsFilter(): void {
    if (this.$route.name === ChatsFilters.CHAT_ARCHIVE_ROUTE) {
      return;
    }

    const draftFilter: FilterInterface = {
      text: 'Draft',
      value: 'review.isDraft',
      icon: 'mdi-progress-pencil',
      type: FilterType.CHECKBOX,
      items: [
        { text: 'True', value: true },
      ],
      selected: { text: 'review.isDraft', value: false },
    };

    this.filters.push(draftFilter);
  }

  private async addTagsFilter(): Promise<void> {
    if (this.$route.name === ChatsFilters.CHAT_QUEUE_ROUTE) {
      return;
    }
    this.tags = await this.tagRepositoryV2.getActiveTags();

    const tagsFilter: FilterInterface = {
      text: 'Tags',
      value: 'tags',
      icon: 'mdi-tag-multiple-outline',
      type: FilterType.COMBOBOX,
      items: this.tags.map((tag) => ({ text: tag.label, value: tag.id })),
      selected: { text: 'tags', value: '' },
    };

    this.filters.push(tagsFilter);
  }

  private async applyFilter(): Promise<void> {
    const filterData: FilterRequestData = {};
    this.filters.forEach((filter) => {
      if (!filter.selected || filter.selected.text === '') {
        return;
      }

      if (filter.value === 'chat.endedTimestamp') {
        filterData[filter.value] = this.getDateFilterData();
        return;
      }

      if (filter.value === 'tags' && filter.selected.value) {
        filterData[filter.value] = (filter.selected.value as unknown as FilterItemInterface[])
          .map((val) => val.value)
          .join(',');
        return;
      }

      filterData[filter.value] = filter.selected.value;
    });

    this.$emit('filter-chats', filterData);
  }

  private getDateFilterData(): string | undefined {
    const dateFilter = this.filters.find((filter) => filter.value === 'chat.endedTimestamp' && filter.selected);
    if (!dateFilter || !dateFilter.selected || !dateFilter.selected.value) {
      return undefined;
    }

    const selected = dateFilter.selected.value as string;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;

      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    startDate.setMinutes(startDate.getTimezoneOffset());
    endDate.setMinutes(endDate.getTimezoneOffset());
    return `${this.convertDateToString(startDate)},${this.convertDateToString(endDate)}`;
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se');
  }

  private async removeFilter(filter: FilterInterface, filterIndex: number, text = ''): Promise<void> {
    this.selectedFilters[filterIndex].selected = { text, value: '' };
    this.selectedFilters = this.selectedFilters.filter((f) => f.text !== filter.text);
    await this.applyFilter();
  }

  private async intersectBooleanFilters(): Promise<void> {
    let intersected = false;
    this.selectedFilters.forEach((selectedFilter, index) => {
      if (selectedFilter.type !== FilterType.CHECKBOX) {
        return;
      }

      this.selectedFilters[index].selected.text = selectedFilter.value;
      this.selectedFilters[index].selected.value = true;

      intersected = true;
    });

    if (!intersected) {
      return;
    }

    await this.applyFilter();
  }

  private getTagColor(tagId: number): string {
    const tag = this.tags.find((tag) => tag.id === tagId);
    if (!tag) {
      return 'gray';
    }

    return tag.color;
  }
}
